<script setup lang="ts">
/**
 * Service Worker Update Prompt
 *
 * Listens for `gleem:sw-update-available` custom events dispatched from
 * main.ts when vite-plugin-pwa detects a new SW version. Shows a dismissible
 * toast with an "Aggiorna" button that applies the update and reloads.
 *
 * No-op when SW is disabled (VITE_ENABLE_SW !== 'true') — no event is ever
 * dispatched and the toast never appears.
 */
import { onMounted, onBeforeUnmount, ref } from 'vue'
import { RefreshCw, X } from 'lucide-vue-next'

const visible = ref(false)
const applying = ref(false)
let updateFn: (() => Promise<void> | void) | null = null

function onUpdateAvailable(e: Event) {
  const detail = (e as CustomEvent<{ update: () => Promise<void> | void }>).detail
  if (detail && typeof detail.update === 'function') {
    updateFn = detail.update
    visible.value = true
  }
}

async function applyUpdate() {
  if (!updateFn || applying.value) return
  applying.value = true
  try {
    await updateFn()
    // updateSW(true) triggers skipWaiting + reload — this line may not run.
  } catch (err) {
    console.error('[SW] Update failed', err)
    applying.value = false
  }
}

function dismiss() {
  visible.value = false
}

onMounted(() => {
  window.addEventListener('gleem:sw-update-available', onUpdateAvailable)
})

onBeforeUnmount(() => {
  window.removeEventListener('gleem:sw-update-available', onUpdateAvailable)
})
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="translate-y-4 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-4 opacity-0"
  >
    <div
      v-if="visible"
      role="status"
      aria-live="polite"
      class="fixed bottom-20 right-4 z-[100] max-w-sm bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
    >
      <div class="p-4 flex items-start gap-3">
        <div class="shrink-0 p-2 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-xl">
          <RefreshCw class="w-5 h-5" :class="{ 'animate-spin': applying }" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-slate-900 dark:text-white">
            Nuova versione disponibile
          </p>
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Aggiorna per ottenere le ultime novità e i bug fix.
          </p>
          <div class="mt-3 flex gap-2">
            <button
              type="button"
              :disabled="applying"
              class="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              @click="applyUpdate"
            >
              {{ applying ? 'Aggiornamento…' : 'Aggiorna' }}
            </button>
            <button
              type="button"
              :disabled="applying"
              class="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
              @click="dismiss"
            >
              Più tardi
            </button>
          </div>
        </div>
        <button
          type="button"
          class="shrink-0 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
          aria-label="Chiudi"
          @click="dismiss"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>
  </Transition>
</template>
